import { curry, cloneDeep, get, set, isArray, map, forEach } from "lodash";
import { DateTime } from "luxon";

import { dateBuilder } from "./date-builder";
import { TIME_FORMAT_12_HOUR, TIME_FORMAT_24_HOUR } from "./date-format.constants";

export const TIME_STRING_REGEX = /^([01]\d|2[0-3]):([0-5]\d)(:[0-5]\d)?$/;

/**
 * Transposes a given date into UTC without changing its displayed local time.
 */
export const toUTCInLocalTimeDt = (dt: DateTime): DateTime => dateBuilder(dt).utcInLocalTime().DateTime;

/**
 * Transposes a given date into UTC without changing its displayed local time.
 */
export const toUTCInLocalTime = (date: Date): Date => dateBuilder(date).utcInLocalTime().Date;

/**
 * Converts a DateTime into its ISO date representation (`yyyy-MM-dd`).
 */
export const toISODateDt = (dt: DateTime): string => dateBuilder(dt).ISODate;

/**
 * Converts a JS Date into its ISO date representation (`yyyy-MM-dd`).
 */
export const toISODate = (date: Date): string => dateBuilder(date).ISODate;

/**
 * Converts a JS Date to a UTC ISO date string (`yyyy-MM-dd`).
 * Useful when a date is stored in UTC and you need the report-date
 * without local-timezone shifting.
 */
export const toUTCISODate = (date: Date): string => DateTime.fromJSDate(date, { zone: "utc" }).toISODate()!;

/**
 * Converts a UTC ISO date string (`yyyy-MM-dd`) to a JS Date at UTC midnight.
 * Inverse of `toUTCISODate`.
 */
export const fromUTCISODate = (isoDate: string): Date =>
  DateTime.fromISO(isoDate, { zone: "utc" }).toJSDate();

/**
 * Returns a DateTime that is `numDays` prior to the provided DateTime.
 */
export const nDaysAgoDt = curry(
  (numDays: number, dt: DateTime): DateTime => dateBuilder(dt).add({ days: numDays * -1 }).DateTime,
);

/**
 * Returns the start of day for the provided JS Date.
 */
export const toStartOfDay = (date: Date): Date => dateBuilder(date).startOf("day").Date;

/**
 * Returns the end of day for the provided JS Date.
 */
export const toEndOfDay = (date: Date): Date => dateBuilder(date).endOf("day").Date;

/**
 * Returns the DateTime that is `numDays` prior to the provided DateTime, expressed in UTC.
 */
export const nDaysAgoInUTCDt = (dt: DateTime, numDays: number): DateTime =>
  dateBuilder(dt)
    .add({ days: numDays * -1 })
    .utc().DateTime;

/**
 * Uses the current local day/month/year to construct a JS Date at UTC midnight.
 */
export const getCurrentLocalDateAtUTCMidnight = () => {
  const now = DateTime.now();
  return DateTime.utc(now.year, now.month, now.day).toJSDate();
};

/**
 * Converts specified string fields to Date objects in a list of records.
 */
export function convertDateStrings<T extends object>(data: T[], datePaths: string[]): T[] {
  // eslint-disable-next-line sonarjs/cognitive-complexity
  return map(data, (item) => {
    const cloned = cloneDeep(item);

    forEach(datePaths, (path) => {
      if (path.includes("[]")) {
        const [arrayPath, subPath] = path.split("[].");
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const arr = get(cloned, arrayPath);

        if (isArray(arr)) {
          arr.forEach((element) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const val = get(element, subPath);
            if (val && typeof val === "string") {
              const parsed = new Date(val);
              if (!isNaN(parsed.valueOf())) {
                set(element, subPath, parsed);
              }
            }
          });
        }
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const val = get(cloned, path);
        if (val && typeof val === "string") {
          const parsed = new Date(val);
          if (!isNaN(parsed.valueOf())) {
            set(cloned, path, parsed);
          }
        }
      }
    });

    return cloned;
  });
}

/**
 * parses a time string (HH:MM or HH:MM:SS) into a DateTime object
 * useful for comparing/sorting times
 */
export function parseTimeString(time: string): DateTime {
  const [hour, minute, second] = time.split(":").map(Number);
  return DateTime.fromObject({ hour, minute, second: second ?? 0 });
}

/**
 * validates a time string matches HH:MM or HH:MM:SS format
 */
export function isValidTimeString(time: string): boolean {
  return TIME_STRING_REGEX.test(time);
}

export const convert24HourTimeTo12HourTime = (time: string) => {
  return DateTime.fromFormat(time, TIME_FORMAT_24_HOUR).toFormat(TIME_FORMAT_12_HOUR);
};
